import {
  DraftHandleValue,
  Editor,
  EditorState,
  Modifier,
  RichUtils,
} from 'draft-js'
import { stateToHTML } from 'draft-js-export-html'
import 'draft-js/dist/Draft.css'
import React, {
  CSSProperties,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
} from 'react-icons/md'
import styled from 'styled-components'
import FieldStyles from '../components/FieldStyles'
import {
  customFontSizes,
  getFontKeyBySize,
  getFontSizeFromHTML,
  hasForbiddenChars,
  maxCharsDisplay,
  maxLinesDisplay,
} from '../helpers/fieldHelper'
import { useNotFirstRender } from '../helpers/firstRender'
import {
  backspaceRemovesBullets,
  createState,
  selectAllText,
  unselectAllText,
} from '../helpers/richHelper'
import colors from '../lib/colors'
import SelectField from './SelectField'

const Controls = styled.div`
  background-color: ${colors.lightestGray};
  position: absolute;
  height: 28px;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  padding-left: 4px;
  border-radius: 4px 4px 0 0;
`
const Control = styled.button`
  all: unset;
  display: inline-flex;
  cursor: pointer;
  svg {
    width: 20px;
    height: auto;
    border-radius: 2px;
    &:hover {
      background-color: ${colors.gray};
    }
  }
`
const Input = styled.div`
  /* specificity required to override */
  &.styled-input {
    position: relative;
    ${({ theme: { isFocused } }): string => (isFocused ? `border-color: ${colors.blue} !important` : '')};
  }
  cursor: text;
  background-color: ${colors.white};
`
const EditorContainer = styled.div`
  min-height: ${({ theme: { height } }): string => height};
  padding-top: 30px;
`
interface IProps {
  className?: string
  style?: CSSProperties
  label?: string
  subtitle?: string
  value?: string
  onChange?: (value: string) => void
  isError?: boolean
  maxChars?: number
  maxLines?: number
  placeholder?: string
  setHeight?: string
  hasFontSize?: true
}
export default function RichField(props: IProps): ReactElement {
  const {
    className,
    style,
    label,
    subtitle,
    value,
    onChange,
    isError,
    maxChars,
    maxLines,
    placeholder,
    setHeight,
    hasFontSize,
  } = props
  const [contentError, setContentError] = useState('')

  // useEffect below with `handleFontSize` can trigger changes on render
  // that we don't actually want to send unless the user has interacted
  const [userInteracted, setUserInteracted] = useState(false)
  const managedChange = (content: string): void => {
    if (userInteracted && onChange) onChange(content)
  }

  const [editorState, setEditorState] = useState<EditorState>(() => {
    if (value) return createState(value)
    return EditorState.createEmpty()
  })

  const [fontStyleMap, fontCustomStyles] = customFontSizes(12, 50)

  const editorFontSize = value && getFontSizeFromHTML(value)
  const editorFontKey = getFontKeyBySize(fontStyleMap, editorFontSize)

  const options = Object.keys(fontStyleMap).map((key) => ({
    value: key,
    label: fontStyleMap[key].fontSize,
  }))
  const [fontSizeKey, setFontSizeKey] = useState<string>(editorFontKey || '')
  const fontValue = fontSizeKey && fontStyleMap[fontSizeKey].fontSize

  const errorClass = isError ? 'error' : ''
  const classes = `${errorClass} ${className}`

  const editorRef = useRef<Editor>(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const focusEditor = (): void => {
    if (editorRef.current) editorRef.current.focus()
  }
  const returnFocus = (): ReturnType<typeof setTimeout> => {
    const timeout = setTimeout(
      () => editorRef.current && editorRef.current.focus(),
      0,
    )
    return timeout
  }

  const toggleStyle = (newStyle: string): EditorState => {
    const selectionState = editorState.getSelection()

    // If no selection has been made, select all text
    if (selectionState.isCollapsed()) {
      const newState = selectAllText(editorState)
      const newStateWithStyle = RichUtils.toggleInlineStyle(newState, newStyle)
      return unselectAllText(newStateWithStyle)
    }

    // If a selection has been made, toggle the style for the selection only
    return RichUtils.toggleInlineStyle(editorState, newStyle)
  }

  const handleFontSize = (newFontStyle: string, state?: EditorState): void => {
    let newState = selectAllText(state || editorState)
    const selectionState = newState.getSelection()

    // Remove existing font size styles
    const currentContent = newState.getCurrentContent()
    const withoutFontSize = Object.keys(fontStyleMap).reduce(
      (contentState, thisFontSize) => {
        const removed = Modifier.removeInlineStyle(
          contentState,
          selectionState,
          thisFontSize,
        )
        return removed
      },
      currentContent,
    )
    newState = EditorState.push(
      newState,
      withoutFontSize,
      'change-inline-style',
    )

    const newStateWithStyle = RichUtils.toggleInlineStyle(
      newState,
      newFontStyle,
    )
    const unselect = unselectAllText(newStateWithStyle)

    setEditorState(unselect)
    setFontSizeKey(newFontStyle)

    const htmlString = stateToHTML(
      unselect.getCurrentContent(),
      fontCustomStyles,
    )

    managedChange(htmlString)
  }

  useEffect(() => {
    // ensure font size is applied on first render
    handleFontSize(fontSizeKey)
    // Deselect after correcting font (timeout ensures asynchronous execution)
    setTimeout(() => editorRef.current?.blur())
  }, [])

  // If we change the value externally, we need to update the field
  const notFirstRender = useNotFirstRender()
  useEffect((): void => {
    if (notFirstRender && !userInteracted && value) {
      const newState = createState(value)
      setEditorState(newState)

      // If we get a new value with an embedded font size, use that
      const newFontSize = getFontSizeFromHTML(value)
      const newFontKey = getFontKeyBySize(fontStyleMap, newFontSize)
      if (newFontKey) {
        setFontSizeKey(newFontKey)
        handleFontSize(newFontKey, newState)
      } else {
        // else reapply the current font size
        handleFontSize(fontSizeKey, newState)
      }
    }
  }, [value])

  const handleReturn = (): DraftHandleValue => {
    const currentContent = editorState.getCurrentContent()
    const currentBlockMap = currentContent.getBlockMap()
    if (maxLines && currentBlockMap.size >= maxLines) {
      setContentError('Over line limit')
      return 'handled'
    }
    return 'not-handled'
  }

  const handleBeforeInput = (
    _: string,
    newState: EditorState,
  ): DraftHandleValue => {
    const newChars = newState.getCurrentContent().getPlainText('')

    if (hasForbiddenChars(newChars)) {
      setContentError('Character not allowed')
      return 'handled'
    }
    if (maxChars && newChars.length >= maxChars) {
      setContentError('Over character limit')
      return 'handled'
    }
    setContentError('')

    return 'not-handled'
  }

  const handlePastedText = (
    text: string,
    _: string,
    newState: EditorState,
  ): DraftHandleValue => {
    const pastedLines = text.split('\n').length

    const currentContent = newState.getCurrentContent()
    const currentBlockMap = currentContent.getBlockMap()

    const newLines = currentBlockMap.size + pastedLines

    const newContent = Modifier.replaceText(
      currentContent,
      newState.getSelection(),
      text,
    )

    if (hasForbiddenChars(newContent.getPlainText(''))) {
      setContentError('Character not allowed')
      return 'handled'
    }
    if (maxChars && newContent.getPlainText('').length >= maxChars) {
      setContentError('Over character limit')
      return 'handled'
    }
    if (maxLines && newLines > maxLines) {
      setContentError('Over line limit')
      return 'handled'
    }
    setContentError('')

    return 'not-handled'
  }

  const chars = editorState.getCurrentContent().getPlainText('').length
  const lines = editorState.getCurrentContent().getBlockMap().size

  const blocks = editorState.getCurrentContent().getBlockMap()
  const hasListItems = blocks.some(
    (block) => block?.getType() === 'unordered-list-item'
      || block?.getType() === 'ordered-list-item',
  )

  const handleChange = (newEditorState: EditorState): void => {
    setEditorState(newEditorState)

    const htmlString = stateToHTML(
      newEditorState.getCurrentContent(),
      fontCustomStyles,
    )

    managedChange(htmlString)
  }

  const handleBold = (): void => {
    const nothingSelected = editorState.getSelection().isCollapsed()
    if (nothingSelected) return

    const newState = toggleStyle('BOLD')
    handleChange(newState)
    returnFocus()
  }

  const handleItalic = (): void => {
    const nothingSelected = editorState.getSelection().isCollapsed()
    if (nothingSelected) return

    const newState = toggleStyle('ITALIC')
    handleChange(newState)
    returnFocus()
  }

  const handleUnorderedList = (): void => {
    const newState = RichUtils.toggleBlockType(
      editorState,
      'unordered-list-item',
    )
    handleChange(newState)
    returnFocus()
  }

  const handleOrderedList = (): void => {
    const newState = RichUtils.toggleBlockType(editorState, 'ordered-list-item')
    handleChange(newState)
    returnFocus()
  }

  const height = setHeight || '86px'

  const inputClasses = `${isError || contentError ? 'error ' : ''}styled-input`

  return (
    <FieldStyles
      className={classes}
      style={style}
      label={label}
      subtitle={subtitle}
      maxChars={maxCharsDisplay(chars, maxChars)}
      maxLines={maxLinesDisplay(lines, maxLines)}
      onClick={(): void => setUserInteracted(true)}
      error={contentError}
    >
      <Input className={inputClasses} theme={{ isFocused }}>
        <Controls>
          <Control onClick={handleBold}>
            <MdFormatBold />
          </Control>
          <Control onClick={handleItalic}>
            <MdFormatItalic />
          </Control>
          <Control onClick={handleUnorderedList}>
            <MdFormatListBulleted
              style={{ width: '17px', marginLeft: '2px' }}
            />
          </Control>
          <Control onClick={handleOrderedList}>
            <MdFormatListNumbered
              style={{ width: '17px', marginLeft: '4px' }}
            />
          </Control>
          {hasFontSize && (
            <SelectField
              options={options}
              onChange={(option): void => {
                if (option) handleFontSize(option.value)
              }}
              placeholder="Font size..."
              isSmall={true}
              style={{ marginLeft: 'auto', marginRight: '3px' }}
              value={
                fontValue && { value: fontValue.toString(), label: fontValue }
              }
            />
          )}
        </Controls>
        <EditorContainer onClick={focusEditor} theme={{ height }}>
          <Editor
            ref={editorRef}
            customStyleMap={fontStyleMap}
            editorState={editorState}
            onChange={handleChange}
            onFocus={(): void => {
              setIsFocused(true)
              setUserInteracted(true)
            }}
            onBlur={(): void => {
              setIsFocused(false)
              setUserInteracted(false)
            }}
            handleReturn={handleReturn}
            handleBeforeInput={handleBeforeInput}
            handlePastedText={handlePastedText}
            placeholder={hasListItems ? '' : placeholder}
            handleKeyCommand={(command: string): DraftHandleValue => {
              if (command === 'backspace') setContentError('')

              const handleValue = backspaceRemovesBullets(
                command,
                editorState,
                setEditorState,
              )
              return handleValue
            }}
          />
        </EditorContainer>
      </Input>
    </FieldStyles>
  )
}
